import { useEffect, useRef, useState, type ReactNode } from 'react'

import type { Episode } from '../../../api/drama'
import { DIRECTION_LOCK_PX, decideSwipe, isVertical, rubberBand } from '../swipe'

interface Props {
  episodes: Episode[]
  index: number
  /**
   * 这一次换集要不要翻页动画。
   *
   * 由页面给，不在这里猜：手指滑出来的那次和「播完自动下一集」都要动画，
   * 而从分集抽屉里跳到第 30 集不要——隔着 29 屏滑过去既慢又没意义，
   * 中间那些屏还根本没渲染。
   */
  animate: boolean
  onIndexChange: (next: number) => void
  /**
   * 长按视频两侧顶倍速、上滑锁定期间为真。
   *
   * 上滑锁定的位移和竖滑切集抢的是同一个信号——32px 的慢速上滑单看数值
   * 够不着 `decideSwipe` 的阈值，但为了不留隐患，由页面显式关掉换集判定，
   * 而不是依赖「数值差得够远，应该不会撞」这种偶然成立的安全边际。
   */
  gestureLocked?: boolean
  /** 每一屏画什么由页面决定：在播的那一集、锁住的那一集、还没取到地址的那一集，长得都不一样。 */
  children: (episode: Episode, isCurrent: boolean) => ReactNode
}

/** 切集动画。再长就显得黏，再短就看不出是「翻」过去的。 */
const SLIDE_MS = 260

/**
 * 竖向的剧集流：一集一屏，跟手上下滑，松手吸附。
 *
 * **跟手是重点**，不是「滑够距离就换一屏」。后者在手上的感觉是点按，
 * 而短剧的用户是在「刷」——手指按住时画面就要跟着走，露出下一集的边，
 * 松手才决定是吸过去还是弹回来。
 *
 * 只渲染当前集和它上下各一屏。62 集全渲染就是 62 个 <video>，
 * 低端安卓直接被解码器拖垮。
 */
export function EpisodeFeed({ episodes, index, animate, onIndexChange, gestureLocked, children }: Props) {
  const [drag, setDrag] = useState(0)
  /** 手指还在屏上。这一段必须没有任何过渡，否则画面比手指慢半拍。 */
  const [dragging, setDragging] = useState(false)
  /**
   * 同一个值再用 ref 存一份。
   *
   * **松手时读的必须是这一份。** 快速轻扫的整个过程只有几十毫秒，
   * React 会把 touchmove 里的 setDrag 批到下一帧，而 touchend 在同一帧就到了——
   * 读 state 拿到的还是 0，于是「一甩就过」的那种滑法一次都切不动，
   * 只有慢慢拖才有反应。手指在屏幕上，代码却说没动。
   */
  const dragRef = useRef(0)
  // 跟手的那一段不能有过渡，否则画面比手指慢半拍；松手后才开启。
  const [sliding, setSliding] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(() => window.innerHeight)

  // 一屏的高是视频区的高（屏幕减去下面的选集黑带），量容器自己。
  // Telegram 的 WebView 展开、键盘收起、横竖屏都会让它变，用固定值会在变化后错位半屏。
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setHeight(el.clientHeight))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const touch = useRef<{ x: number; y: number; at: number; axis: 'unknown' | 'vertical' | 'other' } | null>(null)

  /** 同样的「松手时读 ref」道理：锁没锁，松手那一刻必须读最新值，不能等 state 下一帧。 */
  const gestureLockedRef = useRef(gestureLocked ?? false)
  useEffect(() => {
    gestureLockedRef.current = gestureLocked ?? false
  }, [gestureLocked])

  function moveTo(dy: number) {
    dragRef.current = dy
    setDrag(dy)
  }

  function end() {
    const t = touch.current
    touch.current = null
    setDragging(false)
    if (!t || t.axis !== 'vertical' || gestureLockedRef.current) {
      moveTo(0)
      return
    }
    const intent = decideSwipe(dragRef.current, Date.now() - t.at, height)
    const next = index + (intent === 'next' ? 1 : intent === 'prev' ? -1 : 0)
    setSliding(true)
    moveTo(0)
    if (next !== index && next >= 0 && next < episodes.length) onIndexChange(next)
  }

  return (
    <div
      ref={viewportRef}
      style={styles.viewport}
      onTouchStart={(e) => {
        const p = e.touches[0]
        if (!p) return
        touch.current = { x: p.clientX, y: p.clientY, at: Date.now(), axis: 'unknown' }
        setDragging(true)
        setSliding(false)
      }}
      onTouchMove={(e) => {
        const t = touch.current
        const p = e.touches[0]
        if (!t || !p) return
        // 长按顶速期间整次触摸都不归这里：上滑锁定那 32px 不能带着整屏一起跟手走
        if (gestureLockedRef.current) {
          t.axis = 'other'
          return
        }
        const dx = p.clientX - t.x
        const dy = p.clientY - t.y
        if (t.axis === 'unknown') {
          // 位移还太小，方向看不出来，先不接管——这几像素可能只是点按时的抖动
          if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return
          t.axis = isVertical(dx, dy) ? 'vertical' : 'other'
        }
        if (t.axis !== 'vertical') return
        moveTo(rubberBand(dy, index === 0, index === episodes.length - 1))
      }}
      onTouchEnd={end}
      onTouchCancel={end}
      onWheel={(e) => {
        // 桌面版 Telegram 没有触摸，滚轮一下一集
        if (Math.abs(e.deltaY) < 24 || sliding) return
        const next = index + (e.deltaY > 0 ? 1 : -1)
        if (next < 0 || next >= episodes.length) return
        setSliding(true)
        onIndexChange(next)
      }}
    >
      <div
        style={{
          ...styles.rail,
          transform: `translate3d(0, ${-index * height + drag}px, 0)`,
          // 跟手的那一段没有过渡；松手后的回弹（sliding）和页面发起的换集（animate）才有
          transition:
            !dragging && (sliding || animate)
              ? `transform ${SLIDE_MS}ms cubic-bezier(.22,.61,.36,1)`
              : 'none',
          // 常驻 will-change 会让浏览器一直为这一层留着合成内存，
          // 低端机上那是实打实的几十 MB。只在真的要动的时候给。
          willChange: dragging || sliding || animate ? 'transform' : 'auto',
        }}
        onTransitionEnd={() => setSliding(false)}
      >
        {/*
          **永远只有三个 slot**，和这部剧有多少集无关：当前这一屏，和它上下各一屏。
          62 集全渲染就是 62 个 <video> 加 62 张封面，低端机直接被拖垮。
          key 用集 id，所以滑过去时 React 复用的是同一批节点，不重建。
        */}
        {visibleSlots(episodes, index).map(({ episode, at }) => (
          <div key={episode.id} style={{ ...styles.slot, top: at * height, height }}>
            {children(episode, at === index)}
          </div>
        ))}
      </div>
    </div>
  )
}

/** 当前这一屏和上下各一屏。返回的是「第几集 + 它在第几屏」，位置还按真实下标算。 */
function visibleSlots(episodes: Episode[], index: number): Array<{ episode: Episode; at: number }> {
  const out: Array<{ episode: Episode; at: number }> = []
  for (let at = index - 1; at <= index + 1; at++) {
    const episode = episodes[at]
    if (episode) out.push({ episode, at })
  }
  return out
}

const styles = {
  viewport: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    // 交给我们自己处理，否则 Telegram 的下拉关闭会把竖滑抢走
    touchAction: 'none',
  },
  rail: { position: 'absolute', inset: 0 },
  slot: { position: 'absolute', left: 0, right: 0 },
} satisfies Record<string, React.CSSProperties>
